import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  acceptableMultiLineText,
  WHATSAPP_REPLY_TEXT_MAX,
  type ConversationLead,
  type ReplyWhatsAppBody,
  type ReplyWhatsAppResponse,
  type WhatsAppConversationReply,
  type WhatsAppConversationRow,
  type WhatsAppTemplateKeyCode,
} from "@gioia365/contracts";
import { DAILY_RECIPIENT_CAP, renderTemplate, templateParametersOf, withStopLine } from "@gioia365/domain/whatsapp";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiRequestError } from "../api/http";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { Select } from "../design/components/Select";
import { TextArea } from "../design/components/TextArea";
import "../design/components/text-field.css";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { formatNumericDateTime } from "../i18n/format";
import { useT, type Translate } from "../i18n/locale";
import { withLink } from "../i18n/with-link";
import { formatPhoneForDisplay } from "../leads/lead-display";
import { leadCardQuery, useLeadView } from "../leads/lead-queries";
import { useAccess } from "../session/access";
import type { TabContext } from "../session/tab-context";
import { alreadyDeclared, forgetReplyRequest, replyRequestIdFor, useDeclareWhatsAppSent, useReplyWhatsApp, whatsAppTemplatesQuery } from "./api";
import { inboundKindKey } from "./inbound-kinds";
import { composeMessage, dailyCapWarningOf, recipientOf, templateValuesOf, whatsAppLinkOf } from "./message";
import { ConfirmStep } from "./WhatsAppDialog";
import { fallbackReasonText } from "./whatsapp-texts";
import "./whatsapp.css";

// «Rispondi su WhatsApp» da «Conversazioni → WhatsApp» (piano M6, R3, passo 12b; questioni 419, 421 e 422; ruling R3 «La risposta»;
// mock-up `conversazioni.html`, `#m-wa-reply`, e `mockup.js`). La risposta parte dal numero dell'azienda che ha ricevuto il messaggio del
// Lead («A Giulia Ferri, dal WhatsApp ufficiale di Luca Bianchi.»), anche quando risponde un'altra persona, e va al telefono da cui il
// Lead ha scritto; sotto, il messaggio del Lead («Il Lead ha scritto il …: «…»»). Che cosa offre la finestra, prima del clic, dai fatti
// della riga (`reply`: la fine della finestra di 24 ore per quel numero e quel telefono, il numero da cui parte e se è pronto):
// - il numero è pronto sul WhatsApp ufficiale e il Lead ha scritto nelle ultime 24 ore: «La tua risposta», testo libero, e «Invia»;
//   l'anteprima mostra la riga «scrivi STOP» che il server mette in fondo (questione 421). Allo scadere delle 24 ore, anche a finestra
//   aperta, passa ai modelli;
// - pronto, ma dopo le 24 ore: «Modello approvato da Meta», soltanto i modelli approvati, con l'anteprima e l'avviso del tetto (413);
// - il numero non è pronto (Gioia365 non lo ha più, non è più sul WhatsApp ufficiale, l'azienda ha spento il WhatsApp ufficiale, non è
//   registrato o la sua persona non è attiva), oppure non c'è un modello approvato dopo le 24 ore: la finestra dice perché e offre «Apri
//   WhatsApp» con il testo scritto, dal WhatsApp di chi risponde, come il modo a mano di R1 (`manual: true`; la finestra di Meta non
//   conta), poi «Hai inviato il messaggio?» con «Sì, inviato» (`ConfirmStep` della finestra di R1). Nessun blocco muto.
// Prima di tutto la regola del Lead (D25; revisione del passo 12b), dalla scheda del Lead come nella finestra di R1/R2: con «Non inviare
// WhatsApp» acceso o con uno status che blocca le chiamate la finestra dice il motivo e «Invia» e «Apri WhatsApp» sono spenti (lo
// stato «blocked» di `UX_SYSTEM.md`); il link a mano aspetta la scheda, perché si apre nello stesso clic, prima della risposta del
// server. Dopo le 24 ore la finestra propone i soli modelli approvati con un valore per ogni variabile per questo Lead (gli altri il
// server li terrebbe a mano); il passaggio ai modelli, allo scadere delle 24 ore o perché lo dice il server, si annuncia.
// Il server ricontrolla tutto al clic; le sue risposte che tengono a mano (il ripiego con il perché, il rifiuto di Meta) portano la
// finestra al modo a mano con le parole del perché; una risposta libera fuori dalla finestra (le 24 ore passate mentre si scriveva)
// passa ai modelli. L'invio porta l'identificativo della richiesta nella cache della pagina (`replyRequestIdFor`, ruling E4): dopo una
// rete caduta il nuovo clic ritrova il messaggio già scritto. Durante l'invio la finestra non si chiude (Esc, il fondo, «Chiudi» e
// «Annulla»), come «Invia il WhatsApp» di R2. La conferma automatica dello «STOP» non ha «Rispondi»: la riga non ne porta.

/** La riga a cui si risponde: un messaggio del Lead con il Lead, il telefono da cui ha scritto e «Rispondi». */
export type ReplyTarget = WhatsAppConversationRow & { lead: ConversationLead; leadE164: string; reply: WhatsAppConversationReply };

/** Se la riga offre «Rispondi su WhatsApp». */
export function isReplyTarget(row: WhatsAppConversationRow): row is ReplyTarget {
  return row.lead !== null && row.leadE164 !== null && row.reply !== null && row.message.direction === "in";
}

type Props = {
  tab: TabContext;
  row: ReplyTarget;
  timeZone: string;
  onClose: () => void;
};

type Phase =
  | { kind: "compose" }
  | {
      kind: "confirm";
      /** Il link aperto dal browser. */
      link: string;
      /** Il messaggio preparato a mano; `null` finché il server non risponde. */
      message: { messageId: string; link: string } | null;
      clientRequestId: string;
    };

/** Vero finché la finestra di 24 ore è aperta; allo scadere la finestra si ridisegna da sé. */
function useServiceWindowOpen(endsAt: string): boolean {
  const end = Date.parse(endsAt);
  const [now, setNow] = useState(() => Date.now());
  const open = now < end;
  useEffect(() => {
    if (!open) return;
    // Al più il massimo di `setTimeout`: la finestra è di 24 ore, molto meno.
    const timer = window.setTimeout(() => setNow(Date.now()), Math.min(Math.max(end - Date.now(), 0) + 50, 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [open, end, now]);
  return open;
}

/** Gli identificativi da mettere in `aria-describedby`, oppure niente. */
function describedBy(...ids: (string | null)[]): string | undefined {
  const present = ids.filter((id): id is string => id !== null);
  return present.length > 0 ? present.join(" ") : undefined;
}

/** Perché il server ha tenuto a mano la risposta: il rifiuto di Meta, il suo ripiego con il perché, o il numero passato a mano. */
function heldTextOf(t: Translate, response: ReplyWhatsAppResponse, person: string, templateName: string | null): string {
  if (response.messageId !== null) return t("whatsapp.dialog.metaRefused");
  if (response.route.kind === "send") return t("whatsapp.reply.numberOff", { person });
  switch (response.route.reason) {
    case "no_sender":
      return t("whatsapp.reply.numberGone");
    case "sender_not_ready":
      return t("whatsapp.reply.numberNotReady", { person });
    case "channel_disabled":
      return t("whatsapp.dialog.fallback.channelDisabledMeta");
    case "template_not_approved":
    case "missing_values":
      return (
        fallbackReasonText(t, response.route.reason, { channel: "meta", template: templateName === null ? null : { name: templateName, metaStatus: null } }) ??
        t("whatsapp.dialog.fallback.notReady")
      );
    // I controlli di WhatsApp Agile (piano M6, R9): «Rispondi» non passa mai da Agile (WA-17), quindi non arrivano qui.
    case "recipient_not_allowed":
    case "daily_cap":
    case "paced":
    case "channel_down":
    case "provider_unreachable":
      return t("whatsapp.dialog.fallback.notReady");
  }
}

export function ReplyDialog({ tab, row, timeZone, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const access = useAccess();
  const view = useLeadView();
  const queryClient = useQueryClient();
  const ids = useId();
  const leadId = row.lead.id;
  const inReplyTo = row.message.id;
  const templates = useQuery(whatsAppTemplatesQuery(tab));
  // La scheda del Lead dà i valori dei modelli per l'anteprima, il conteggio di oggi e il nome dello status che blocca.
  const cardQuery = useQuery(leadCardQuery(tab, leadId, view));
  const card = cardQuery.data;
  const reply = useReplyWhatsApp(tab, leadId, view);
  const declare = useDeclareWhatsAppSent(tab, view);
  const windowOpen = useServiceWindowOpen(row.reply.windowEndsAt);
  // Aperta dentro le 24 ore: se poi passa ai modelli, la finestra lo annuncia.
  const [openedWithin] = useState(windowOpen);
  const [text, setText] = useState("");
  const [chosenKey, setChosenKey] = useState<WhatsAppTemplateKeyCode | null>(null);
  // Il perché del modo a mano detto dal server (il suo ripiego, il rifiuto di Meta): da lì in poi la risposta si apre a mano.
  const [held, setHeld] = useState<string | null>(null);
  // Il server ha detto che le 24 ore sono passate, anche se l'orologio del browser non lo diceva ancora.
  const [windowClosed, setWindowClosed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Il Lead non riceve WhatsApp secondo il server (la scheda era vecchia): come la regola della scheda.
  const [serverBlock, setServerBlock] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "compose" });
  const textRef = useRef<HTMLTextAreaElement>(null);
  const sentRef = useRef<HTMLButtonElement>(null);

  // Tenuta a mano dal server, la risposta si scrive: il focus va sul testo, sotto il perché.
  useEffect(() => {
    if (held !== null) textRef.current?.focus();
  }, [held]);

  const ready = phase.kind === "confirm" && phase.message !== null;
  // Registrato il messaggio a mano, il focus va su «Sì, inviato», come nella finestra di R1.
  useEffect(() => {
    if (ready) sentRef.current?.focus();
  }, [ready]);

  const via = row.reply.via;
  const person = via?.person.displayName ?? row.person?.displayName ?? "";
  const officialReady = via !== null && via.channel === "meta" && via.enabledByCompany && via.ready;
  const within = windowOpen && !windowClosed;
  const data = templates.data;
  const approved = data?.templates.filter((template) => template.approved) ?? [];
  // I valori dei modelli per il telefono da cui il Lead ha scritto; i modelli che si possono mandare: approvati e, con la scheda, con un
  // valore per ogni variabile (come `templateParametersOf` del server, che altrimenti la terrebbe a mano, `missing_values`).
  const phone = card?.lead.phones.find((candidate) => candidate.e164 === row.leadE164) ?? null;
  const values = card ? templateValuesOf(card, phone ? recipientOf(card, phone) : null, access.context) : null;
  const usable = values === null ? approved : approved.filter((template) => templateParametersOf(template.body, values).missing.length === 0);
  // Il modo della finestra, dai fatti della riga e dalle risposte del server (in cima al file).
  let mode: "text" | "template" | "manual";
  if (held !== null || !officialReady) mode = "manual";
  else if (within) mode = "text";
  else if (data !== undefined && usable.length === 0) mode = "manual";
  else mode = "template";

  let why: string | null = null;
  if (held !== null) why = held;
  else if (via === null) why = t("whatsapp.reply.numberGone");
  else if (via.channel !== "meta") why = t("whatsapp.reply.numberOff", { person });
  else if (!via.enabledByCompany) why = t("whatsapp.dialog.fallback.channelDisabledMeta");
  else if (!via.ready) why = t("whatsapp.reply.numberNotReady", { person });
  else if (mode === "manual" && approved[0] !== undefined) why = t("whatsapp.dialog.fallback.missingValues", { template: approved[0].name });
  else if (mode === "manual") why = t("whatsapp.reply.noApproved");

  // La regola del Lead, dalla scheda (in cima al file): il primo motivo della scheda è lo status che blocca, poi l'interruttore.
  let leadBlock: string | null = serverBlock;
  if (leadBlock === null && card?.whatsapp.reason === "do_not_contact") leadBlock = t("whatsapp.reasons.do_not_contact", { status: card.lead.status.name });
  else if (leadBlock === null && card && card.whatsapp.optOut !== null) leadBlock = t("whatsapp.reasons.whatsapp_opt_out");

  // Il testo libero come lo salva il server (senza i caratteri che rifiuta, senza spazi ai bordi) e con la riga «scrivi STOP» in fondo.
  const stopLine = data?.stopLine ?? "";
  const body = acceptableMultiLineText(text).trim();
  const composed = body === "" ? "" : stopLine.trim() === "" ? body : withStopLine(body, stopLine);
  const tooLong = mode !== "template" && composed.length > WHATSAPP_REPLY_TEXT_MAX;
  // A mano il link si apre nello stesso clic: aspetta la riga «scrivi STOP» e la scheda del Lead (la sua regola).
  const link = mode === "manual" && leadBlock === null && body !== "" && data !== undefined && card !== undefined && !tooLong ? whatsAppLinkOf(row.leadE164, composed) : null;

  // Il modello, dopo le 24 ore: l'anteprima con i valori puliti del Lead (il telefono da cui ha scritto), come lo riceverà da Meta.
  const template = usable.find((candidate) => candidate.key === chosenKey) ?? usable[0] ?? null;
  let preview = composed;
  if (mode === "template") {
    const cleaned = template && values ? templateParametersOf(template.body, values).values : null;
    preview = template && cleaned ? composeMessage(renderTemplate(template.body, cleaned).text, cleaned, stopLine).text : "";
  }
  const capWarning = mode === "template" && template !== null && card !== undefined && dailyCapWarningOf(card.whatsapp.todayCount, template);

  // L'invio dal WhatsApp ufficiale (la risposta a mano non aspetta il server: la domanda arriva subito).
  const sending = reply.isPending && phase.kind === "compose" && mode !== "manual";
  const textId = `${ids}-text`;
  const whyId = `${ids}-why`;
  const problemId = `${ids}-problem`;
  const capId = `${ids}-cap`;
  const sendingId = `${ids}-sending`;
  const blockId = `${ids}-block`;
  const waitId = `${ids}-wait`;
  const windowId = `${ids}-window`;
  // Passata ai modelli a finestra aperta (allo scadere delle 24 ore o per il server): lo si annuncia.
  const switchedToTemplates = mode === "template" && openedWithin;

  /** Esc, il fondo, «Chiudi» e «Annulla»: durante l'invio no (in cima al file). */
  function close() {
    if (!sending) onClose();
  }

  /**
   * Un rifiuto del server: le 24 ore passate portano ai modelli; il Lead che non riceve WhatsApp spegne la finestra con il motivo; un
   * numero non ammesso (fuori dalla produzione) porta al modo a mano, come nella finestra di R2; gli altri si dicono con le parole della
   * situazione.
   */
  function refused(failure: unknown) {
    if (failure instanceof ApiRequestError && failure.code === "whatsapp_blocked") {
      const reason = failure.details?.reason;
      if (reason === "outside_service_window") {
        setWindowClosed(true);
        return;
      }
      if (reason === "whatsapp_opt_out") return setServerBlock(t("whatsapp.reasons.whatsapp_opt_out"));
      if (reason === "do_not_contact") return setServerBlock(card ? t("whatsapp.reasons.do_not_contact", { status: card.lead.status.name }) : messageForError(t, failure));
    }
    if (failure instanceof ApiRequestError && failure.code === "whatsapp_recipient_not_allowed") {
      setHeld(messageForError(t, failure));
      return;
    }
    setError(refusalText(failure));
  }

  function refusalText(failure: unknown): string {
    if (failure instanceof ApiRequestError && failure.code === "validation_failed") {
      const fields: unknown[] = Array.isArray(failure.details?.fields) ? failure.details.fields : [];
      if (fields.includes("inReplyTo")) return t("whatsapp.reply.phoneGone");
      if (fields.includes("text") || fields.includes("templateKey")) return t("whatsapp.dialog.tooLong", { max: WHATSAPP_REPLY_TEXT_MAX });
    }
    return messageForError(t, failure);
  }

  function requireText() {
    setError(t("whatsapp.reply.required"));
    textRef.current?.focus();
  }

  /** «Invia»: dal WhatsApp ufficiale, il testo libero dentro le 24 ore o il modello dopo (in cima al file). */
  async function send() {
    if (sending || leadBlock !== null) return;
    let request: ReplyWhatsAppBody;
    if (mode === "text") {
      if (body === "") {
        requireText();
        return;
      }
      if (tooLong) return;
      // Il testo nella chiave: dopo una rete caduta lo stesso testo ritrova lo stesso messaggio, un testo corretto è una richiesta nuova.
      request = { inReplyTo, clientRequestId: replyRequestIdFor(queryClient, { tab, leadId, inReplyTo, kind: `text:${body}` }), text: body };
    } else if (mode === "template" && template) {
      request = { inReplyTo, clientRequestId: replyRequestIdFor(queryClient, { tab, leadId, inReplyTo, kind: `template:${template.key}` }), templateKey: template.key };
    } else return;
    setError(null);
    let response: ReplyWhatsAppResponse;
    try {
      response = await reply.mutateAsync(request);
    } catch (failure) {
      refused(failure);
      return;
    }
    if (response.messageId !== null && response.status !== "failed") {
      // L'avviso del tetto che la finestra non aveva detto (il conteggio della scheda era vecchio), ripetuto dal server (413).
      if (response.dailyCapWarning && !capWarning) toast.show(t("whatsapp.dialog.sentToMetaCap", { cap: DAILY_RECIPIENT_CAP }), "warning");
      else toast.show(t("whatsapp.dialog.sentToMeta"), "success");
      onClose();
      return;
    }
    setHeld(heldTextOf(t, response, person, mode === "template" ? (template?.name ?? null) : null));
  }

  /** «Apri WhatsApp»: il link si apre nello stesso clic, e intanto il server registra la risposta preparata a mano (come R1). */
  function openManual() {
    if (link === null || leadBlock !== null) return;
    const opened = link;
    const clientRequestId = replyRequestIdFor(queryClient, { tab, leadId, inReplyTo, kind: `manual:${composed}` });
    setError(null);
    // La risposta del server, se arriva prima della domanda.
    let settled: { messageId: string; link: string } | null = null;
    // La domanda arriva dopo il clic: il link resta nella pagina finché il browser non ha aperto la scheda nuova.
    const swap = window.setTimeout(() => setPhase({ kind: "confirm", link: opened, message: settled, clientRequestId }), 0);
    reply.mutateAsync({ inReplyTo, clientRequestId, text: body, manual: true }).then(
      (response) => {
        const { messageId } = response;
        if (messageId === null || response.link === null) {
          window.clearTimeout(swap);
          setPhase({ kind: "compose" });
          setError(t("errors.unexpected"));
          return;
        }
        const message = { messageId, link: response.link };
        settled = message;
        setPhase((current) => (current.kind === "confirm" && current.message === null ? { ...current, message } : current));
      },
      (failure: unknown) => {
        window.clearTimeout(swap);
        setPhase({ kind: "compose" });
        refused(failure);
      },
    );
  }

  async function declareSent() {
    if (phase.kind !== "confirm" || phase.message === null || declare.isPending) return;
    const { messageId } = phase.message;
    setError(null);
    try {
      await declare.mutateAsync(messageId);
    } catch (failure) {
      // Già dichiarato (da un'altra scheda, dalla Timeline): è quello che si voleva.
      if (!alreadyDeclared(failure)) {
        setError(messageForError(t, failure));
        return;
      }
    }
    forgetReplyRequest(queryClient, phase.clientRequestId);
    toast.show(t("whatsapp.dialog.sentDone"), "success");
    onClose();
  }

  function notSent() {
    if (phase.kind !== "confirm" || phase.message === null) return;
    toast.show(t("whatsapp.dialog.notSentDone"));
    onClose();
  }

  const alertBox = error ? (
    <Reason tone="warning" role="alert">
      {error}
    </Reason>
  ) : null;

  // Una finestra sola per i due passi: la domanda prende il posto della risposta, come nella finestra di R1.
  if (phase.kind === "confirm") {
    return (
      <Dialog
        open
        size="wide"
        title={t("whatsapp.reply.title")}
        onClose={onClose}
        footer={
          <>
            <Button onClick={notSent} disabled={phase.message === null}>
              {t("whatsapp.dialog.notSent")}
            </Button>
            <Button ref={sentRef} variant="primary" onClick={() => void declareSent()} disabled={phase.message === null || declare.isPending} aria-busy={declare.isPending ? "true" : undefined}>
              {t("whatsapp.dialog.sent")}
            </Button>
          </>
        }
      >
        <ConfirmStep preparing={phase.message === null} otherText={phase.message !== null && phase.message.link !== phase.link} reopen={phase.message?.link ?? phase.link} alert={alertBox} />
      </Dialog>
    );
  }

  const leadName = <strong>{row.lead.displayName ?? formatPhoneForDisplay(row.leadE164)}</strong>;
  const when = formatNumericDateTime(timeZone, row.message.at);
  const kindKey = inboundKindKey(row.message.contentKind);
  const problem = tooLong ? t("whatsapp.dialog.tooLong", { max: WHATSAPP_REPLY_TEXT_MAX }) : null;
  const loadingTemplates = data === undefined && !templates.isError;

  // A mano, finché la scheda del Lead non è arrivata, il link aspetta: lo si dice accanto al pulsante.
  const waiting = mode === "manual" && leadBlock === null && card === undefined;
  let action: ReactNode;
  if (leadBlock !== null) {
    action = (
      <Button variant="primary" disabled aria-describedby={blockId}>
        <Icon name="whatsapp" />
        {t(mode === "manual" ? "whatsapp.dialog.open" : "whatsapp.reply.send")}
      </Button>
    );
  } else if (mode !== "manual") {
    action = (
      <Button
        variant="primary"
        disabled={sending || tooLong || (mode === "template" && template === null)}
        aria-busy={sending ? "true" : undefined}
        aria-describedby={describedBy(problem ? problemId : null, capWarning ? capId : null)}
        onClick={() => void send()}
      >
        <Icon name="whatsapp" />
        {t("whatsapp.reply.send")}
      </Button>
    );
  } else if (link !== null) {
    action = (
      <a
        className="g-button"
        data-variant="primary"
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        aria-describedby={describedBy(why ? whyId : null)}
        onClick={openManual}
        // Il clic con la rotella apre anche lui una scheda nuova.
        onAuxClick={(event) => {
          if (event.button === 1) openManual();
        }}
      >
        <Icon name="whatsapp" />
        {t("whatsapp.dialog.open")}
      </a>
    );
  } else {
    // Senza testo il clic chiede di scriverlo (il mock-up); con un testo troppo lungo, o finché la riga «scrivi STOP» non è arrivata,
    // il pulsante è spento con il perché scritto accanto.
    const empty = body === "";
    action = (
      <Button
        variant="primary"
        disabled={!empty}
        aria-describedby={describedBy(why ? whyId : null, problem ? problemId : null, waiting ? waitId : null)}
        onClick={empty ? requireText : undefined}
      >
        <Icon name="whatsapp" />
        {t("whatsapp.dialog.open")}
      </Button>
    );
  }

  return (
    <Dialog
      open
      size="wide"
      title={t("whatsapp.reply.title")}
      onClose={close}
      footer={
        <>
          <Button onClick={close} disabled={sending} aria-describedby={sending ? sendingId : undefined}>
            {t("common.cancel")}
          </Button>
          {action}
        </>
      }
    >
      <div className="g-stack g-whatsapp">
        <p>{withLink(t(mode === "manual" ? "whatsapp.reply.toManual" : "whatsapp.reply.to", { person }), leadName)}</p>
        <blockquote className="g-whatsapp__preview g-whatsapp__quote">
          {kindKey === null ? t("whatsapp.reply.wrote", { when, text: row.message.body }) : t("whatsapp.reply.wroteKind", { kind: t(kindKey), when })}
          {kindKey !== null && row.message.body !== "" ? <span className="g-whatsapp__caption">{t("conversations.whatsapp.quoted", { text: row.message.body })}</span> : null}
        </blockquote>
        {leadBlock !== null ? (
          <Reason tone="warning" role={serverBlock !== null ? "alert" : undefined} id={blockId}>
            {leadBlock}
          </Reason>
        ) : null}
        {why && leadBlock === null ? (
          <Reason tone={held !== null ? "warning" : "info"} role={held !== null ? "alert" : undefined} id={whyId}>
            {why}
          </Reason>
        ) : null}
        {switchedToTemplates && leadBlock === null ? (
          <Reason tone="warning" role="alert" id={windowId}>
            {t("whatsapp.reasons.outside_service_window")}
          </Reason>
        ) : null}
        {alertBox}
        {leadBlock !== null ? null : mode === "template" ? (
          loadingTemplates ? (
            <p className="g-field__hint">{t("common.loading")}</p>
          ) : templates.isError ? (
            <div className="g-whatsapp__row">
              <Reason tone="warning" role="alert">
                {messageForError(t, templates.error)}
              </Reason>
              <Button disabled={templates.isFetching} onClick={() => void templates.refetch()}>
                {t("common.retry")}
              </Button>
            </div>
          ) : (
            <Select
              id={`${ids}-template`}
              label={t("whatsapp.reply.template")}
              value={template?.key ?? ""}
              options={usable.map((candidate) => ({ value: candidate.key, label: candidate.name }))}
              {...(switchedToTemplates ? { describedBy: windowId } : { hint: t("whatsapp.reasons.outside_service_window") })}
              disabled={sending}
              onChange={(value) => setChosenKey(usable.find((candidate) => candidate.key === value)?.key ?? null)}
            />
          )
        ) : (
          <TextArea
            ref={textRef}
            id={textId}
            label={t("whatsapp.reply.text")}
            className="g-whatsapp__text"
            value={text}
            maxLength={WHATSAPP_REPLY_TEXT_MAX}
            disabled={sending}
            hint={mode === "text" ? t("whatsapp.reply.withinWindow") : undefined}
            describedBy={problem ? problemId : undefined}
            onChange={(event) => {
              setError(null);
              setText(event.target.value);
            }}
          />
        )}
        {preview !== "" && leadBlock === null ? (
          <div className="g-field">
            <p id={`${ids}-preview`} className="g-field__label">
              {t("whatsapp.dialog.preview")}
            </p>
            <blockquote className="g-whatsapp__preview" aria-labelledby={`${ids}-preview`}>
              {preview}
            </blockquote>
          </div>
        ) : null}
        {waiting ? (
          cardQuery.isError ? (
            <div className="g-whatsapp__row">
              <Reason tone="warning" role="alert" id={waitId}>
                {messageForError(t, cardQuery.error)}
              </Reason>
              <Button disabled={cardQuery.isFetching} onClick={() => void cardQuery.refetch()}>
                {t("common.retry")}
              </Button>
            </div>
          ) : (
            <p className="g-field__hint" role="status" id={waitId}>
              {t("common.loading")}
            </p>
          )
        ) : null}
        {mode !== "template" && templates.isError ? (
          <div className="g-whatsapp__row">
            <Reason tone="warning" role="alert">
              {messageForError(t, templates.error)}
            </Reason>
            <Button disabled={templates.isFetching} onClick={() => void templates.refetch()}>
              {t("common.retry")}
            </Button>
          </div>
        ) : null}
        {problem ? (
          <Reason tone="warning" role="alert" id={problemId}>
            {problem}
          </Reason>
        ) : null}
        {capWarning && card ? (
          <Reason tone="warning" id={capId}>
            {t("whatsapp.dialog.dailyCap", { count: card.whatsapp.todayCount, cap: DAILY_RECIPIENT_CAP })}
          </Reason>
        ) : null}
        {sending ? (
          <p className="g-field__hint" role="status" id={sendingId}>
            {t("whatsapp.dialog.sending")}
          </p>
        ) : null}
        {mode === "manual" && leadBlock === null ? <p className="g-field__hint">{t("whatsapp.dialog.manualNote")}</p> : null}
      </div>
    </Dialog>
  );
}
